import { useState } from 'react';

import clsx from 'clsx';
import Portal from '@srcDS/js/components/atoms/portal';
import styles from './Toolbar.module.scss';
import FilterModalContent from './desktop/FilterModalContent';
import { useSticky } from './useSticky';
import { useModal } from './desktop/useModal';
import { FilterTag } from '@srcDS/js/components/molecules/filterTag/FilterTag';
import { DesktopFilterTitles } from './desktop/DesktopFilterTitles';
import { MobileFilterTitles } from './mobile/MobileFilterTitles';
import SortTitle from './desktop/SortTitle';
import { MobileFilterDrawer } from './mobile/MobileFilterDrawer';
import React from 'react';
import { FilterFacet } from './types';
import { useMedia } from 'react-use';

const Toolbar = ({ filterTagsData, facets, sortShown, setSortShown, sortComponent, isMobile, sortState }) => {
  const [activeFilter, setActiveFilter] = useState<FilterFacet | null>(null);
  const [drawerShown, setDrawerShown] = useState(false);
  const titleRef = React.useRef<HTMLDivElement>(null);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const isDesktop = useMedia('(min-width: 1024px)', false);

  const { isSticky, toolbarSentinelRef } = useSticky();

  const { openFilterModal, closeModal, modalPosition, initModalPosition } = useModal({
    setActiveFilter,
    setSortShown,
    enableClickOutside: isDesktop,
    modalRef,
  });

  const toggleSortModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setActiveFilter(null);
    setSortShown((prev) => !prev);
    initModalPosition(event);
  };

  const scrollToTop = () => {
    document.getElementById('categoryPage').parentElement?.scrollIntoView({
      behavior: 'smooth',
      inline: 'end',
    });
  };

  const handleCloseModal = (event) => {
    scrollToTop();
    closeModal(event);
  }

  const rootStyle = clsx(styles.root, {
    [styles.isPinned]: isSticky,
  });

  return (
    <>
      <div ref={toolbarSentinelRef} style={{ height: '1px', marginTop: '-1px' }} />

      {/* FILTER TITLES */}
      <div
        ref={titleRef}
        className={rootStyle}
        onClick={() => scrollToTop()}
      >
        <div className={styles.leftBlock}>
          <div className={styles.filters}>
            {isMobile ? (
              <MobileFilterTitles
                setDrawerShown={() => {
                  setDrawerShown(true);
                  setActiveFilter(null);
                }}
                setSortShown={setSortShown}
              />
            ) : (
              <DesktopFilterTitles
                facets={facets}
                activeFilter={activeFilter}
                closeModal={closeModal}
                openFilterModal={openFilterModal}
              />
            )}
          </div>
        </div>
        <div className={styles.rightBlock}>
          {!isMobile && (
            <SortTitle
              sortShown={sortShown}
              activeFilter={activeFilter}
              toggleSortModal={toggleSortModal}
              sortState={sortState}
            />
          )}
        </div>
      </div>

      {/* MODAL */}
      {!isMobile && (activeFilter || sortShown) && modalPosition && (
        <Portal renderInto="filterPopup">
          <div
            className={styles.modalAnchor}
            style={{ top: modalPosition.top, left: modalPosition.left - (sortShown ? 80 : 0) }}
          >
            <div className={styles.modalContent} ref={modalRef}>
              {activeFilter && <FilterModalContent activeFilter={activeFilter} closeModal={handleCloseModal} />}
              {sortShown && <div className={styles.sortWrapper}>{sortComponent}</div>}
            </div>
          </div>
        </Portal>
      )}

      {/* DRAWER */}
      {isMobile && drawerShown && (
        <MobileFilterDrawer
          drawerShown={drawerShown}
          setDrawerShown={setDrawerShown}
          facets={facets}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          filterTagsData={filterTagsData}
          callback={scrollToTop}
        />
      )}

      {/* APPLIED FILTERS */}
      {!isMobile && filterTagsData?.length > 0 && (
        <div className={styles.filterTagWrapper}>
          <span className={styles.filterTagTitle}>FILTERS APPLIED:</span> <FilterTag filters={filterTagsData} />
        </div>
      )}
    </>
  );
};

export default Toolbar;
